import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { exportChatExcel } from "../../services/chatApi";

const ORANGE = "#F07828";
const BLUE = "#1565C0";
const BORDER = "rgba(25,118,210,0.20)";
const PREVIEW_ROW_LIMIT = 20;

function buildExportFileName(section) {
  const raw =
    String(section?.title || section?.module || "chat-export")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || "chat-export";

  return `${raw}-${Date.now()}.xlsx`;
}

const ResultSection = ({ section }) => {
  const [exporting, setExporting] = useState(false);

  const allRows = Array.isArray(section?.rows) ? section.rows : [];
  const previewRows = allRows.slice(0, PREVIEW_ROW_LIMIT);

  const fields = Array.isArray(section?.fields) ? section.fields : [];
  const headers = fields.length
    ? fields
    : allRows.length
    ? Object.keys(allRows[0]).map((x) => ({ fieldCode: x, fieldLabel: x }))
    : [];

  const canExport = allRows.length > 0;

  const previewRowCount = Number(
    section?.previewRowCount || previewRows.length || 0
  );

  const totalRows = Number(
    section?.totalRows || allRows.length || previewRowCount
  );

  const showPreviewNote =
    Boolean(section?.isPreview) && totalRows > previewRowCount;

  const exportFileName = buildExportFileName(section);

  const handleExport = async () => {
    try {
      setExporting(true);
  
      const exportPayload = {
        section: {
          ...section,
          toolCode: section?.toolCode,
          filters: section?.filters || {},
          fields: Array.isArray(section?.fields)
            ? section.fields.map((f) => f.fieldCode || f)
            : [],
          groupBy: section?.groupBy || [],
          sortBy: section?.sortBy || [],
          isExport: true,
        },
      };
  
      const blobData = await exportChatExcel(exportPayload);
  
      const blob = new Blob([blobData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
  
      const url = window.URL.createObjectURL(blob);
  
      const link = document.createElement("a");
      link.href = url;
      link.download = exportFileName;
  
      document.body.appendChild(link);
      link.click();
  
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("EXPORT FAILED:", err);
  
      const errorText =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Export failed";
  
      alert(errorText);
    } finally {
      setExporting(false);
    }
  };

  if (section?.type === "error") {
    return (
      <Paper sx={{ mt: 1, p: 1, bgcolor: "#fff4f4" }}>
        <Typography variant="subtitle2">{section.title}</Typography>
        <Typography variant="body2">{section.message}</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        mt: 1,
        p: 1,
        border: `1px solid ${BORDER}`,
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {section?.title}
        </Typography>

        <Tooltip title={exporting ? "Exporting..." : "Download Excel"}>
          <span>
            <IconButton
              size="small"
              onClick={handleExport}
              disabled={!canExport || exporting}
              sx={{
                color: BLUE,
                "&:hover": {
                  color: ORANGE,
                  bgcolor: "rgba(240,120,40,0.08)",
                },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {allRows.length === 0 ? (
        <Typography variant="body2">No records found.</Typography>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 250 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableCell key={h.fieldCode}>
                      {h.fieldLabel || h.fieldCode}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {headers.map((h) => (
                      <TableCell key={h.fieldCode}>
                        {String(row[h.fieldCode] ?? row[h.fieldLabel] ?? "")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {showPreviewNote && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "#666" }}
            >
              Showing first {previewRowCount} of {totalRows} rows. Use the
              download icon to export all rows.
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default ResultSection;